const ProductConfiguratorFieldRestoredEvent = new CustomEvent(
  "product-configurator:field-restored",
  { bubbles: true }
);

class ProductConfigurator extends VariantRadios {
  constructor() {
    super();

    this.productRoot = this.closest("article");
    this.summaryList = this.querySelector("ul[data-summary]");

    this.initSteps();
    this.buildMedia();
    this.buildHeaderForMobile();

    this.productRoot.addEventListener("click", this.onClick.bind(this));
    this.addEventListener("keyup", this.onKeyUp.bind(this));
  }

  onClick(ev) {
    let elementWithHref = ev.target.getAttribute("href")
      ? ev.target
      : ev.target.closest("[href^='#']");

    this.setCharmSide(ev.target);

    if (elementWithHref) {
      ev.preventDefault();
      this.openStep(elementWithHref.getAttribute("href"));
    }
  }

  onChange(ev) {
    if (ev.target.type != "radio") return;

    this.activateLabelsForSelectedOption(ev.target);
    this.setVariant(ev.target);

    this.processAriaControls(ev.target);
    this.processStep(ev.target);
    this.processUpdatables(ev.target);
    this.setProductImages();
    this.setSummaryList();
  }

  onKeyUp(ev) {
    this.processStep(ev.target);
    this.setSummaryList();
  }

  processAriaControls(el) {
    const ariaControlsTarget = document.getElementById(
      el.getAttribute("aria-controls")
    );
    const ariaControlsTargetExpanded = !!el.dataset.ariaControlsExpanded;
    const ariaControlsTargetIsRequired = !!el.dataset.ariaControlsIsRequired;
    const ariaControlsTargetRequiredElement =
      ariaControlsTarget?.querySelector("[data-required]");

    if (ariaControlsTarget) {
      ariaControlsTarget.ariaExpanded = ariaControlsTargetExpanded;

      if (ariaControlsTargetRequiredElement) {
        ariaControlsTargetRequiredElement.disabled =
          !ariaControlsTargetIsRequired;
        ariaControlsTargetRequiredElement.required =
          ariaControlsTargetIsRequired;
      }
    }
  }

  initSteps() {
    this.steps = this.querySelectorAll(".pc__step");
    this.steps.forEach((step) => {
      step
        .querySelectorAll(".cta--secondary")
        .forEach((cta) => this.processStep(cta));
    });
  }

  openStep(id) {
    const activeStepAnchor = this.querySelector(id);
    const activeStepContent = this.querySelector(`${id}-content`);

    if (!activeStepContent) {
      return;
    }

    const activeStepNavLink = this.querySelector(
      `[aria-controls="${activeStepContent.id}"]`
    );

    this.querySelectorAll([
      '.pc__step[aria-expanded="true"]',
      'nav [aria-expanded="true"]',
    ]).forEach((el) => {
      el.ariaExpanded = false;
    });

    document.body.scrollIntoView();

    activeStepAnchor.parentNode
      .querySelector(".active")
      ?.classList?.remove("active");
    activeStepAnchor.classList.add("active");

    activeStepContent.ariaExpanded = true;
    if (activeStepNavLink) activeStepNavLink.ariaExpanded = true;
  }

  processStep(el) {
    const step = el.closest(".pc__step");
    const options = step.querySelectorAll(".pc__options li");
    const requiredFieldsSelector =
      '[type="radio"][data-summary]:checked, [type="text"][data-summary][required]';
    const validFieldsSelector =
      '[type="radio"][data-summary]:checked, [type="text"][data-summary][required]:valid';
    const stepButton = step.querySelector(".cta--secondary");
    const navLink = this.querySelector(`[aria-controls=${step.id}]`);
    const optionsAllValid = [...options].every((option) => {
      const requiredFields = option.querySelectorAll(requiredFieldsSelector);
      const validFields = option.querySelectorAll(validFieldsSelector);
      return (
        requiredFields.length > 0 && requiredFields.length == validFields.length
      );
    });

    stepButton.ariaDisabled = !optionsAllValid;
    if (navLink) navLink.ariaDisabled = !optionsAllValid;
  }

  variantsForTitleGeneration() {
    return Array.from(
      this.querySelectorAll([
        'radio-select[data-selected-value] input[type="radio"]:not([name*=properties]):checked',
        'radio-select:not([data-selected-value]) input[type="radio"]:not([name*=properties])[data-checked]',
      ])
    );
  }

  setVariantUrl() {}

  processUpdatables(el) {
    if (!el.closest("[data-update]")) return;
    if (
      (!el.closest("[data-previous-value]") &&
        el.closest("[data-selected-value]")) ||
      el.closest("[data-previous-value]")?.dataset?.previousValue == el.value
    )
      return;

    const url = new URL(location.href);
    url.searchParams.set("variant", this.selectedVariantOption.value);
    url.searchParams.set(
      "section_id",
      el.closest("[data-section-id").dataset.sectionId
    );

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const newDocument = new DOMParser().parseFromString(text, "text/html");

        document
          .querySelectorAll(`[data-update]`)
          .forEach((existingUpdatable) => {
            const newRestorable = newDocument.querySelector(
              `[data-update="${existingUpdatable.dataset.update}"]`
            );
            const checkedRadioId = existingUpdatable.querySelector(
              "[type=radio]:checked"
            )?.id;
            const existingInputs = existingUpdatable.querySelectorAll(
              "[data-update-restorable][type=text]"
            );

            existingUpdatable.innerHTML = newRestorable.innerHTML;

            const newCheckedRadio = document.getElementById(checkedRadioId);
            if (newCheckedRadio) {
              newCheckedRadio.checked = true;
              newCheckedRadio.dispatchEvent(RadioSelectCheckedEvent);
            }

            existingInputs.forEach((existingInput) => {
              const newInput = document.getElementById(existingInput.id);
              if (!newInput) return;
              newInput.value = existingInput.value;
              newInput.dispatchEvent(ProductConfiguratorFieldRestoredEvent);
            });

            this.processStep(existingUpdatable);
          });

        let clickedElement = document.getElementById(el.id);
        clickedElement.checked = true;
        clickedElement.dispatchEvent(RadioSelectCheckedEvent);
      });
  }

  buildMedia() {
    this.productMediaRoot = this.productRoot.querySelector(".product__media");
    this.flipButtonTemplate = this.querySelector("template[data-flip-button]");

    if (this.flipButtonTemplate) {
      this.flipButton = new DOMParser()
        .parseFromString(
          this.querySelector("template[data-flip-button]").innerHTML,
          "text/html"
        )
        .querySelector("a");

      this.productMediaRoot.append(this.flipButton);
    }

    this.buildHeaderForMobile();
    this.productImages = Array.from(
      this.productRoot.querySelectorAll("better-image:not([data-placeholder])")
    );
  }

  buildHeaderForMobile() {
    if (this.header) return;

    this.header = this.querySelector(".product__configurator__header");
    this.header.append(this.productMediaRoot.cloneNode(true));

    this.zoomInButton = this.header.querySelector("dialog-opener");
    this.zoomInButton.addEventListener(
      "click",
      this.prepareMobileDialogForOpen.bind(this)
    );

    this.headerDialog = this.header.querySelector("dialog");
    this.headerDialog.append(this.buyButton.cloneNode(true));
  }

  prepareMobileDialogForOpen() {
    this.headerDialog.querySelector(".product__media")?.remove();
    this.headerDialog.append(this.productMediaRoot.cloneNode(true));
  }

  setProductImages() {
    const attributesToLookAt = [
      "data-gold-color",
      "data-charm-type",
      "data-language",
      "data-gemstone",
      "data-enamel-color",
      "data-charm-back",
      "data-chain",
      "data-halo",

      "data-letter-size",
      "data-letter-count",
    ];

    let requiredFields = Array.from(
      this.querySelectorAll("radio-select[data-selected-value] :checked")
    );
    let defaultRadios = Array.from(
      this.querySelectorAll(
        "radio-select:not([data-selected-value]) [data-default]"
      )
    );

    // Hide all images
    this.productImages.forEach((image) =>
      image.toggleAttribute("hidden", true)
    );
    // Show only matching images
    this.productImages.forEach((image) => {
      let image_attributes = [...image.attributes].filter((attribute) => {
        return attributesToLookAt.includes(attribute.name);
      });

      if (image_attributes.length == 0) {
        return;
      }

      let everyTrue = image_attributes.every((image_attribute) => {
        if (image_attribute.value == "") {
          return (
            requiredFields.find((radio) => {
              return !!radio.attributes[image_attribute.name];
            }) ||
            defaultRadios.find((radio) => {
              return !!radio.attributes[image_attribute.name];
            })
          );
        }

        return (
          requiredFields.find((radio) => {
            return (
              radio.attributes[image_attribute.name]?.value ==
              image_attribute.value
            );
          }) ||
          defaultRadios.find((radio) => {
            return (
              radio.attributes[image_attribute.name]?.value ==
              image_attribute.value
            );
          })
        );
      });

      if (everyTrue) {
        image.show();
      }
    });
  }

  setCharmSide(el) {
    let charmSideToLookAt =
      el.dataset.side || el.closest("[data-side]")?.dataset?.side || "front";

    if (charmSideToLookAt == "toggle") {
      this.charmSide = this.charmSide == "back" ? "front" : "back";
    } else {
      this.charmSide = charmSideToLookAt;
    }

    if (this.charmSide == "back") {
      this.charmBackSideSeen = true;
    }

    this.productRoot.querySelectorAll(".flippable").forEach((flippable) => {
      flippable.classList.toggle("flipped-once", !!this.charmBackSideSeen);
      flippable.classList.toggle("flipped", this.charmSide == "back");
    });
  }

  setSummaryList() {
    this.summaryList.innerHTML = "";
    Array.from(
      this.querySelectorAll(
        'input[type="radio"][data-summary]:checked, input[type="text"][data-summary][required]:valid'
      )
    ).forEach((input) => {
      let summary = decodeURI(input.dataset.summary.replace(/\+/, " "));
      let option_name = input.dataset.optionName;
      let liForInput =
        this.summaryList.querySelector(`[data-option='${option_name}']`) ??
        document.createElement("li");

      liForInput.dataset.option = option_name;
      liForInput.innerHTML = `${summary.replace("$value", input.value)}`;
      this.summaryList.append(liForInput);
    });
  }
}

customElements.define("product-configurator", ProductConfigurator);

class ProductConfiguratorStep {}
